import type { ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { PageHeader } from '@/components/admin/AdminShell';
import { EmptyState } from '@/components/admin/states';
import { HELP_CONTENT } from '@/features/help/content';
import { diagramsFor } from '@/features/help/diagrams';
import { HelpDiagrams, VisualSteps } from '@/features/help/HelpDiagrams';
import { flowsFor } from '@/features/help/flows';
import { HELP_TOPICS } from '@/features/help/topics';

/**
 * 画面ごとに独立した操作マニュアルページ（例: /help/members）。
 * 各画面のPageHeaderにある「使い方を見る」リンクは、topics.ts の topicForPath で
 * 現在地から解決したこのページへ直接リンクする。
 * 権限バッジやロール別の比較は表示しない（読み手は自分の操作だけを知りたい
 * のであって、他ロールの権限一覧は不要という利用者フィードバック）。
 */
export function HelpTopicPage(): ReactElement {
  const { topicId = '' } = useParams();
  const { user } = useAuth();
  const topic = HELP_TOPICS.find((t) => t.id === topicId);
  const content = HELP_CONTENT[topicId];
  const role = user?.active_role;
  const allowed = topic && role && topic.roles.includes(role);

  if (!topic || !content || !role || !allowed) {
    return (
      <>
        <PageHeader title="操作マニュアル" />
        <EmptyState
          title="このページのマニュアルは見つかりませんでした"
          description="左メニューの「操作マニュアル」から一覧を確認してください。"
          action={
            <Link to="/help" className="text-wpaccent hover:underline">
              ← マニュアル一覧に戻る
            </Link>
          }
        />
      </>
    );
  }

  const summary = content.summary[role] ?? content.summary.fallback;
  const flows = flowsFor(topicId, role);
  const diagrams = diagramsFor(topicId, role);
  const procedures = content.procedures.filter((p) => !p.roles || p.roles.includes(role));
  const notes = (content.notes ?? []).filter((n) => !n.roles || n.roles.includes(role));

  return (
    <>
      <PageHeader
        title={`${topic.label}の使い方`}
        description="上の模式図と「全体の流れ」で順番をつかんでから、下の手順を1番から進めてください。ボタンの名前は、実際の画面と同じ書き方にしています。"
        actions={
          <Link to="/help" className="text-wpaccent hover:underline">
            ← マニュアル一覧
          </Link>
        }
      />

      <div className="flex flex-col gap-4">
        <section className="rounded-sm border border-wpborder bg-white px-4 py-4">
          <p className="text-wptext-strong">{summary}</p>
        </section>

        <HelpDiagrams diagrams={diagrams} />

        {/* 仕上がるまでの流れと、その後に起きること（D-093）。
            手順の羅列だけでは「順に進めるのか、一気に埋めるのか」が読めないため、
            冒頭で全体像を1本の流れとして見せる */}
        {flows.map((flow) => (
          <section
            key={flow.steps.join('|')}
            className="rounded-sm border border-wpborder bg-white px-4 py-4"
          >
            <h2 className="mb-3 font-semibold text-wptext-strong">
              {flow.title ?? '全体の流れ'}
            </h2>
            <ol className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-stretch">
              {flow.steps.map((step, index) => (
                <li
                  key={step}
                  className="flex flex-col gap-2 lg:min-w-[10rem] lg:max-w-[14rem] lg:flex-1 lg:flex-row lg:items-stretch"
                >
                  <div className="flex min-w-0 flex-1 items-start gap-2 rounded-sm border-2 border-wpaccent bg-wpcanvas/60 px-2.5 py-2">
                    <span
                      aria-hidden="true"
                      className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-wpaccent text-xs font-semibold text-white"
                    >
                      {index + 1}
                    </span>
                    <span className="text-wptext-strong">{step}</span>
                  </div>
                  {index < flow.steps.length - 1 && (
                    <>
                      <span
                        aria-hidden="true"
                        className="self-center text-lg font-semibold text-wpaccent lg:hidden"
                      >
                        ↓
                      </span>
                      <span
                        aria-hidden="true"
                        className="hidden shrink-0 self-center text-lg font-semibold text-wpaccent lg:inline"
                      >
                        →
                      </span>
                    </>
                  )}
                </li>
              ))}
            </ol>
            {flow.after && (
              <p className="mt-3 rounded-sm border border-wpborder bg-wpcanvas/40 px-3 py-2 text-wptext-muted">
                <span className="font-semibold text-wptext-strong">この後: </span>
                {flow.after}
              </p>
            )}
          </section>
        ))}

        {procedures.map((procedure) => (
          <section
            key={procedure.title}
            className="rounded-sm border border-wpborder bg-white px-4 py-4"
          >
            <h2 className="mb-3 font-semibold text-wptext-strong">{procedure.title}</h2>
            <VisualSteps rows={procedure.steps.map((step) => ({ body: step }))} />
          </section>
        ))}

        {notes.length > 0 && (
          <section className="rounded-sm border border-wpborder bg-white px-4 py-4">
            <h2 className="mb-3 font-semibold text-wptext-strong">つまずいたとき</h2>
            <ul className="flex flex-col gap-2">
              {notes.map((note, index) => (
                <li
                  key={note.text}
                  className="flex gap-3 rounded-sm border border-wpborder bg-wpcanvas/40 px-3 py-2"
                >
                  <span
                    aria-hidden="true"
                    className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-wpborder bg-white text-xs font-semibold text-wptext-muted"
                  >
                    !
                  </span>
                  <span className="text-wptext-muted">
                    <span className="sr-only">注意 {index + 1}: </span>
                    {note.text}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </>
  );
}
